import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useEffect, useState } from "react";
import { CartItem } from "../../models/cart-item.ts";
import { Order } from "../../models/order";
import { OrderStatus } from "../../models/order-status.ts";
import {
  changeOrderStatus,
  deleteOrder,
  getOrders,
  markOrderViewed,
} from "../../Services/order.service.ts";
import styles from "./OrderList.module.css";

function OrderList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    const fetchOrders = async () => {
      const result = await getOrders();
      if (result.success && result.data) {
        setOrders(result.data);
      } else {
        setOrders([]);
      }
    };
    fetchOrders();
  }, []);

  const getStatusClassName = (status: OrderStatus) => {
    const baseClass = styles.status;
    switch (status) {
      case OrderStatus.Pending:
        return `${baseClass} ${styles.statusPending}`;
      case OrderStatus.Processing:
        return `${baseClass} ${styles.statusProcessing}`;
      case OrderStatus.Shipped:
        return `${baseClass} ${styles.statusShipped}`;
      case OrderStatus.Delivered:
        return `${baseClass} ${styles.statusDelivered}`;
      case OrderStatus.Cancelled:
        return `${baseClass} ${styles.statusCancelled}`;
      default:
        return baseClass;
    }
  };

  const handleChangeStatus = (orderId: string, newStatus: OrderStatus) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId
          ? { ...order, status: newStatus, updatedAt: new Date() }
          : order
      )
    );
    changeOrderStatus(orderId, newStatus)
      .then((response) => {
        if (response.success) {
          setSnackbar({
            open: true,
            message: `Order status changed to ${
              newStatus.charAt(0).toUpperCase() + newStatus.slice(1)
            }`,
            severity: "success",
          });
        }
      })
      .catch((error) => {
        console.error("Failed to change order status:", error);
        setSnackbar({
          open: true,
          message: "Failed to change order status",
          severity: "error",
        });
      });
  };

  const handleDeleteOrder = (orderId: string) => {
    setOrderToDelete(orderId);
    setShowDeletePopup(true);
  };

  const confirmDelete = () => {
    if (orderToDelete) {
      deleteOrder(orderToDelete)
        .then((response) => {
          if (response.success) {
            setOrders((prevOrders) =>
              prevOrders.filter((order) => order.id !== orderToDelete)
            );
            setSnackbar({
              open: true,
              message: "Order removed from dashboard",
              severity: "success",
            });
          } else {
            setSnackbar({
              open: true,
              message: "Failed to delete order",
              severity: "error",
            });
          }
        })
        .catch((error) => {
          console.error("Failed to delete order:", error);
          setSnackbar({
            open: true,
            message: "Failed to delete order",
            severity: "error",
          });
        });
    }
    setShowDeletePopup(false);
    setOrderToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setOrderToDelete(null);
  };

  const money = (amount: number) =>
    `Rs ${Math.round(Number(amount) || 0).toLocaleString()}`;

  const unitPrice = (item: CartItem) => {
    const sale = Number(item.sale) || 0;
    if (sale > 0) return item.productPrice * (1 - sale / 100);
    return item.productPrice;
  };

  const formatWhen = (value?: Date) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-PK", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openOrder = (orderId: string) => {
    const closing = openOrderId === orderId;
    setOpenOrderId(closing ? null : orderId);
    if (closing) return;

    const order = orders.find((item) => item.id === orderId);
    if (!order || order.viewed) return;

    setOrders((prev) =>
      prev.map((item) =>
        item.id === orderId ? { ...item, viewed: true } : item,
      ),
    );
    markOrderViewed(orderId).then((response) => {
      if (!response.success) {
        setOrders((prev) =>
          prev.map((item) =>
            item.id === orderId ? { ...item, viewed: false } : item,
          ),
        );
      }
    });
  };

  const filteredOrders = orders.filter((order) => {
    const customer = order.customerInfo;
    const address = customer?.address;
    const haystack = [
      order.id,
      customer?.firstName,
      customer?.lastName,
      customer?.phoneNumber,
      customer?.email,
      address?.street,
      address?.city,
      address?.zipCode,
      address?.country,
      ...(order.items || []).map((item) => item.productName),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch =
      searchTerm === "" || haystack.includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Order Management</h1>
        {orders.some((order) => !order.viewed) && (
          <p className={styles.newCount}>
            {orders.filter((order) => !order.viewed).length} new
          </p>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Status</label>
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value={OrderStatus.Pending}>Pending</option>
            <option value={OrderStatus.Processing}>Processing</option>
            <option value={OrderStatus.Shipped}>Shipped</option>
            <option value={OrderStatus.Delivered}>Delivered</option>
            <option value={OrderStatus.Cancelled}>Cancelled</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search</label>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by order ID, customer name, phone, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.list}>
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => {
            const customer = order.customerInfo;
            const address = customer?.address;
            const items = order.items || [];
            const open = openOrderId === order.id;
            const pieces = items.reduce(
              (sum, item) => sum + (Number(item.quantity) || 0),
              0,
            );
            return (
              <article
                key={order.id}
                className={order.viewed ? styles.order : `${styles.order} ${styles.unseen}`}
              >
                <button
                  type="button"
                  className={styles.summary}
                  onClick={() => openOrder(order.id)}
                >
                  <span>
                    <strong>
                      {customer?.firstName} {customer?.lastName}
                      {!order.viewed && <i className={styles.newBadge}>New</i>}
                    </strong>
                    <em>{formatWhen(order.createdAt)}</em>
                  </span>
                  <span>
                    {address?.city || "—"}
                    {address?.country ? `, ${address.country}` : ""}
                  </span>
                  <span>
                    {pieces} {pieces === 1 ? "piece" : "pieces"}
                  </span>
                  <span>{money(order.totalPrice)}</span>
                  <span className={getStatusClassName(order.status)}>
                    {order.status}
                  </span>
                  <span className={styles.chevron}>{open ? "Hide" : "View"}</span>
                </button>
                {open && (
                  <div className={styles.detail}>
                    <div className={styles.meta}>
                      <div>
                        <h3>Order</h3>
                        <p>
                          <b>ID</b> {order.id}
                        </p>
                        <p>
                          <b>Placed</b> {formatWhen(order.createdAt)}
                        </p>
                        <p>
                          <b>Updated</b> {formatWhen(order.updatedAt)}
                        </p>
                        <p>
                          <b>Pieces</b> {order.quantity || pieces}
                        </p>
                        <p>
                          <b>Total</b> {money(order.totalPrice)}
                        </p>
                      </div>
                      <div>
                        <h3>Customer</h3>
                        <p>
                          <b>Name</b> {customer?.firstName} {customer?.lastName}
                        </p>
                        <p>
                          <b>Phone</b> {customer?.phoneNumber || "—"}
                        </p>
                        <p>
                          <b>Email</b> {customer?.email || "—"}
                        </p>
                      </div>
                      <div>
                        <h3>Delivery address</h3>
                        <p>
                          <b>Street</b> {address?.street || "—"}
                        </p>
                        <p>
                          <b>City</b> {address?.city || "—"}
                        </p>
                        <p>
                          <b>Postcode</b> {address?.zipCode || "—"}
                        </p>
                        <p>
                          <b>Country</b> {address?.country || "—"}
                        </p>
                      </div>
                    </div>
                    <h3>Items</h3>
                    <div className={styles.items}>
                      {items.map((item) => (
                        <div key={item.id} className={styles.item}>
                          {item.frontImg ? (
                            <img src={item.frontImg} alt="" />
                          ) : (
                            <span className={styles.noImg}>No image</span>
                          )}
                          <div>
                            <strong>{item.productName}</strong>
                            <p>
                              Size {item.size || "—"} · Qty {item.quantity}
                              {item.sale ? ` · ${item.sale}% off` : ""}
                            </p>
                            <p className={styles.colorLine}>
                              <i style={{ background: item.color || "#ccc" }} />
                              {item.color || "No color"}
                            </p>
                          </div>
                          <div className={styles.itemPrice}>
                            <span>{money(unitPrice(item) * item.quantity)}</span>
                            {item.sale ? (
                              <s>{money(item.productPrice * item.quantity)}</s>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={styles.actions}>
                      <label>
                        Status
                        <select
                          value={order.status}
                          onChange={(event) =>
                            handleChangeStatus(
                              order.id,
                              event.target.value as OrderStatus,
                            )
                          }
                        >
                          {Object.values(OrderStatus).map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => handleDeleteOrder(order.id)}
                      >
                        Delete order
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateText}>No orders found</div>
            <div className={styles.emptyStateSubtext}>
              {searchTerm || statusFilter !== "all"
                ? "Try another search or status."
                : "Orders will appear here once customers place them."}
            </div>
          </div>
        )}
      </div>

      {showDeletePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h4>Delete order</h4>
            <p className={styles.deleteMessage}>
              This removes the order from the dashboard. The order record stays
              saved in the database.
            </p>
            <div className={styles.popupActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default OrderList;
